import { Search } from "lucide-react";
import { useState } from "react";

import type { JDSkill } from "@/api/jd-queries";
import { Button } from "@/components/ui/button";
import { Drawer } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { useDraft } from "@/lib/jd-draft-state";

/**
 * Which concepts of a skill's stack the JD wants — DESIGN §20.5. A JD that asks for React seldom wants
 * every concept the bank holds under it, so a skill can be narrowed to some. Nothing ticked means all of
 * them, which is what every skill did before this existed. Apply joins the page's unsaved changes
 * (jd-draft.tsx), so the JD is re-matched once, on Save. A drawer rather than a row opening inside the
 * skills table, which pushed every row below it down the page.
 */
function Picker({ skill, onDone }: { skill: JDSkill; onDone: () => void }) {
  const draft = useDraft();
  const [chosen, setChosen] = useState<Set<string>>(new Set(skill.concepts));
  const [find, setFind] = useState("");
  const options = skill.concept_options;
  const needle = find.trim().toLowerCase();
  const total = options.reduce((n, o) => n + o.questions, 0);
  const selected = options.filter((o) => chosen.has(o.name)).reduce((n, o) => n + o.questions, 0);
  // Chosen ones stay listed whatever the search says, so a concept cannot be ticked and then lost from view.
  const picked = options.filter((o) => chosen.has(o.name));
  const others = options.filter((o) => !chosen.has(o.name) && o.name.toLowerCase().includes(needle));

  function toggle(name: string) {
    const next = new Set(chosen);
    if (!next.delete(name)) next.add(name);
    setChosen(next);
  }

  const row = (o: { name: string; questions: number }) => (
    <label
      key={o.name}
      className="border-bank-surface-3 flex items-center gap-2.5 border-b py-1.5 text-[13px] last:border-b-0"
    >
      <input
        type="checkbox"
        className="accent-bank-accent size-4"
        checked={chosen.has(o.name)}
        onChange={() => toggle(o.name)}
      />
      <span className="truncate">{o.name}</span>
      <span className="text-bank-muted ml-auto font-mono text-[11px] tabular-nums">{o.questions}</span>
    </label>
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <p className="text-[13px]">
          Tick what this role needs.{" "}
          <span className="text-bank-muted">
            Leave everything unticked to use every concept of{" "}
            <span className="font-mono text-xs">{skill.stack_path}</span>.
          </span>
        </p>
        <p className="bg-bank-accent-wash text-bank-accent-ink rounded-md px-2.5 py-2 text-[12.5px]">
          {chosen.size === 0 ? (
            <>
              Using all <strong className="font-mono">{total}</strong> questions.
            </>
          ) : (
            <>
              <strong className="font-mono">
                {selected} of {total}
              </strong>{" "}
              questions, in {chosen.size} concept{chosen.size === 1 ? "" : "s"}.
            </>
          )}
        </p>
        <label className="relative block">
          <Search className="text-bank-muted absolute top-2 left-2.5 size-4" aria-hidden="true" />
          <Input
            type="search"
            className="pl-8"
            aria-label={`Find a concept of ${skill.stack_path}`}
            placeholder={`Find among ${options.length} concepts`}
            value={find}
            onChange={(e) => setFind(e.target.value)}
          />
        </label>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {picked.length > 0 && (
            <>
              <p className="text-bank-muted py-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Chosen
              </p>
              {picked.map(row)}
              <p className="text-bank-muted pt-3 pb-1 font-mono text-[10.5px] tracking-[0.07em] uppercase">
                Others in this stack
              </p>
            </>
          )}
          {others.map(row)}
          {others.length === 0 && needle && (
            <p className="text-bank-muted py-2 text-xs">No concept matches “{find}”.</p>
          )}
        </div>
      </div>
      <div className="border-bank-border flex flex-wrap items-center gap-2 border-t px-4 py-3">
        <Button size="sm" variant="ghost" disabled={chosen.size === 0} onClick={() => setChosen(new Set())}>
          Use all concepts
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={onDone}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              draft.patchSkill(skill.id, { concepts: [...chosen] });
              onDone();
            }}
          >
            Apply
          </Button>
        </div>
        <p className="text-bank-muted w-full text-xs">
          Apply adds this to your unsaved changes; Save changes writes it.
        </p>
      </div>
    </div>
  );
}

export function ConceptsDrawer({
  skill,
  stackChanged,
  onClose,
}: {
  skill: JDSkill | null;
  stackChanged: boolean;
  onClose: () => void;
}) {
  return (
    <Drawer open={skill !== null} onClose={onClose} title={skill ? `Concepts for ${skill.name}` : ""}>
      {skill &&
        (stackChanged ? (
          <p className="text-bank-muted p-4 text-sm">
            The stack was changed and not saved yet, so its concepts are not known. Save the changes first,
            then choose concepts.
          </p>
        ) : skill.concept_options.length === 0 ? (
          <p className="text-bank-muted p-4 text-sm">
            The bank has no concept-tagged questions under {skill.stack_path} yet, so there is nothing to
            choose from.
          </p>
        ) : (
          <Picker key={skill.id} skill={skill} onDone={onClose} />
        ))}
    </Drawer>
  );
}
